import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { errMsg, formatDate, useApi } from "@/lib/api";
import KeyTest from "./KeyTest";

const LINKS = { anthropic: "https://console.anthropic.com/settings/keys", openai: "https://platform.openai.com/api-keys", gemini: "https://aistudio.google.com/apikey" };

function SourceToggle({ provider, k, onChange }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const pick = async (v) => {
    if (v === k.source) return;
    setBusy(true);
    try { await api.put("/settings", { ai: { key_source: { [provider]: v } } }); await onChange(); toast.success(v === "own" ? `${k.label}: ora usa la tua chiave personale` : `${k.label}: ora usa la Universal Key`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const opt = (v, label, dis) => (
    <button type="button" disabled={busy || dis} onClick={() => pick(v)} data-testid={`key-source-${provider}-${v}`} aria-pressed={k.source === v}
      className={`px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${k.source === v ? "bg-navy-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{label}</button>
  );
  return <div className="inline-flex rounded-lg border overflow-hidden" role="group">{opt("universal", "Universal Key")}{opt("own", "Chiave personale", !k.configured)}</div>;
}

export default function ProviderKey({ provider, k, onChange }) {
  const api = useApi();
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); setVal(""); await onChange(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const save = () => act(() => api.put(`/settings/keys/${provider}`, { key: val.trim() }), "Chiave salvata (cifrata sul server). Provala con «Testa chiave personale».");
  const remove = () => act(() => api.delete(`/settings/keys/${provider}`), "Chiave rimossa: il provider usa la Universal Key");
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`provider-key-${provider}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-slate-900">{k.label}</p>
        <SourceToggle provider={provider} k={k} onChange={onChange} />
      </div>
      <p className="text-xs text-slate-600 flex items-center gap-1.5" data-testid={`provider-key-status-${provider}`}>
        <KeyRound size={13} className="text-slate-400" />
        {k.configured ? <>Chiave personale salvata <span className="font-mono">{k.masked}</span>{k.updated_at ? ` · aggiornata ${formatDate(k.updated_at, true)}` : ""}</> : "Nessuna chiave personale salvata"}
        {" · "}in uso: <b className="text-slate-800">{k.source === "own" ? "chiave personale" : "Universal Key Emergent"}</b>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="password" autoComplete="off" value={val} onChange={(e) => setVal(e.target.value)} className="max-w-xs h-9" placeholder={k.configured ? "Incolla una nuova chiave per sostituirla" : `Incolla la chiave (${k.hint})`} data-testid={`provider-key-input-${provider}`} />
        <Button size="sm" variant="outline" onClick={save} disabled={busy || val.trim().length < 20} data-testid={`provider-key-save-${provider}`}>{busy ? <Loader2 size={13} className="animate-spin" /> : "Salva chiave"}</Button>
        {k.configured && <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setConfirm(true)} disabled={busy} data-testid={`provider-key-remove-${provider}`}><Trash2 size={14} className="mr-1" />Rimuovi</Button>}
        <a href={LINKS[provider]} target="_blank" rel="noreferrer" className="text-[11px] text-sky-700 hover:underline">Dove trovo la chiave?</a>
      </div>
      <div className="flex flex-col gap-2">
        <KeyTest provider={provider} source="universal" label="Testa Universal Key" />
        {k.configured && <KeyTest provider={provider} source="own" label="Testa chiave personale" />}
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={`Rimuovere la chiave ${k.label}?`} confirmLabel="Rimuovi" destructive
        description="La chiave viene cancellata dal server. Le prossime operazioni di questo provider useranno la Universal Key Emergent." onConfirm={() => { setConfirm(false); remove(); }} />
    </div>
  );
}
